import React, { useState, useRef } from 'react';
import { getAnchorId, cn } from '@/helpers/utils';
import {
  IconChevronUp,
  IconChevronDown,
  IconChevronRight,
} from '@americanexpress/dls-icons';
import styles from './PartnerNavigation.module.scss';

interface MenuItem {
  type: 'chef' | 'partner' | 'faq';
  name?: string;
  title?: string;
  question?: string;
}

interface MenuSection {
  menuSectionHeader: string;
  items: MenuItem[];
}

interface PartnerNavigationProps {
  menus: MenuSection[];
}

// Main component
const PartnerNavigation: React.FC<PartnerNavigationProps> = ({ menus }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeAnchor, setActiveAnchor] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Listen for scroll to update active nav link on desktop
  React.useEffect(() => {
    const handleScroll = () => {
      // Get all anchor ids in order
      const anchorIds: string[] = [];
      menus.forEach((section) => {
        section.items.forEach((item, idx) => {
          anchorIds.push(
            getAnchorId({
              type: item.type,
              name: item.name,
              title: item.title,
              idx,
              section: section.menuSectionHeader,
            })
          );
        });
      });
      // Find the anchor currently in view
      let found: string | null = null;
      anchorIds.forEach((anchorId) => {
        const el = document.querySelector(`#${anchorId}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          const isAtOrAboveHalfScreen = rect.top <= window.innerHeight / 2;
          if (isAtOrAboveHalfScreen) {
            found = anchorId;
          }
        }
      });
      setActiveAnchor(found);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [menus]);

  // Scroll to anchor on click, using window.scrollTo with offset to account for floating header
  const handleAnchorClick =
    (anchorId: string) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      const el = document.querySelector(`#${anchorId}`);
      if (el) {
        const y = el.getBoundingClientRect().top + window.pageYOffset - 120;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
      setActiveAnchor(anchorId);
    };

  // Render menu items (mobile-first, override with lg_ for desktop)
  const renderMenuItems = (items: MenuItem[], section: string) =>
    items.map((item: MenuItem, idx: number) => {
      const anchorId = getAnchorId({
        type: item.type,
        name: item.name,
        title: item.title,
        idx,
        section,
      });
      const linkText = item.title || item.name;
      if (!linkText) return null;
      // Add active class on desktop if this is the active anchor
      const isActive = activeAnchor === anchorId;
      const baseClass =
        'tw-justify-between lg_tw-max-w-48 tw-w-full lg_tw-border-solid lg_tw-border-b lg_tw-border-gray-300 hover:lg_tw-bg-gray-50 focus:lg_tw-outline-none tw-flex tw-cursor-pointer tw-items-center tw-bg-none tw-px-0 tw-py-3 tw-text-left tw-font-sans tw-text-base tw-font-medium tw-leading-6 tw-text-blue-bright-shade-600-s8 lg_tw-bg-none lg_tw-py-4 lg_tw-text-xs lg_tw-font-bold lg_tw-leading-5';
      const activeClass = isActive
        ? 'lg_tw-text-blue-bright-shade-600-s8'
        : 'lg_tw-text-gray-700';
      return (
        <button
          key={anchorId}
          onClick={handleAnchorClick(anchorId)}
          aria-label={linkText}
          type="button"
          className={`${baseClass} ${activeClass}`}
        >
          <span className="tw-font-inherit tw-block tw-break-words tw-pr-4">
            {linkText}
          </span>
          {/* Chevron only on desktop */}
          <IconChevronRight
            size="xs"
            isFilled={true}
            className={`${styles.iconFillMinimal} tw-absolute tw-right-4 tw-hidden lg_tw-inline`}
          />
        </button>
      );
    });
  // Mobile-first: render both, hide/show with Tailwind responsive classes
  return (
    <>
      {/* Mobile Navigation (below 1024px) */}
      <div className="tw-relative tw-col-span-8 tw-col-start-3 tw-mb-6 tw-block lg_tw-hidden">
        <div
          ref={menuRef}
          className="tw-relative tw-w-full tw-min-w-0 tw-rounded-2xl tw-bg-gray-100 tw-transition-all tw-duration-300 tw-ease-in-out"
        >
          {/* Menu items container with animated max-height */}
          <div
            className="tw-overflow-hidden tw-pt-6 tw-transition-all tw-duration-300 tw-ease-in-out"
            style={{ maxHeight: mobileOpen ? '2000px' : '328px' }}
          >
            {menus.map((section: MenuSection) => (
              <div key={section.menuSectionHeader} className="tw-px-6 tw-pb-6">
                <div className="tw-mb-2 tw-font-sans tw-text-base tw-font-bold tw-leading-6 tw-text-gray-700 lg_tw-text-gray-900">
                  {section.menuSectionHeader}
                </div>
                {renderMenuItems(section.items, section.menuSectionHeader)}
              </div>
            ))}
          </div>
          {/* Show More/Less button always below menu items, only visible on mobile */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            type="button"
            aria-label={mobileOpen ? 'Show Less' : 'Show More'}
            className="tw-mt-0 tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-b-2xl tw-border-t tw-border-solid tw-border-gray-300 tw-bg-gray-100 tw-pb-4dot5 tw-pl-6 tw-pr-2 tw-pt-4dot25 tw-text-base tw-leading-6 tw-text-gray-800"
          >
            <span>{mobileOpen ? 'Show Less' : 'Show More'}</span>
            <span className="tw-absolute tw-right-4 tw-top-1/2 -tw-translate-y-1/2">
              {mobileOpen ? (
                <IconChevronUp
                  size="xs"
                  isFilled={true}
                  color="tw-blue-bright-600"
                />
              ) : (
                <IconChevronDown
                  size="xs"
                  isFilled={true}
                  color="tw-blue-bright-600"
                />
              )}
            </span>
          </button>
        </div>
      </div>

      {/* Desktop Navigation (1024px and above) */}
      <nav
        className="tw-hidden tw-w-full tw-pr-6 lg_tw-sticky lg_tw-block"
        style={{ top: '90px' }}
        aria-label="Partner Navigation"
      >
        {menus.map((section: MenuSection, idx) => (
          <div key={section.menuSectionHeader}>
            <div
              className={cn(
                'tw-mb-2 tw-font-sans tw-text-xs tw-font-bold tw-leading-6 tw-text-gray-700 lg_tw-text-gray-900',
                { 'tw-mt-6': idx !== 0 }
              )}
            >
              {section.menuSectionHeader}
            </div>
            {renderMenuItems(section.items, section.menuSectionHeader)}
          </div>
        ))}
      </nav>
    </>
  );
};

export default PartnerNavigation;
